<template>
  <section id="state-per-capita">
    <figure class="chart">
      <div class="row clearfix">
        <h1>
          Market Share and Per Capita Results
          <!-- <a href="#" data-table="state-per-capita-table" class="infotoggle inline tooltipstered">Click for more info</a> -->
        </h1>

        <ul class="switches">
          <li v-for="(year, index) in years" :key="index">
            <a href="javascript:;" :class="{active: activeYear2==year}" @click="activeYear2=year">{{ year }}</a>
          </li>
        </ul>

        <p class="smallprint" style="text-align: center;">Share of population is shown by bubble size</p>

        <div class="chart-wrapper">
          <highcharts v-if="bubbleChart" class="graphcontainer" :options="bubbleChart"></highcharts>
          <div class="nb">NB: Numbers may not total 100.0% due to rounding.</div>
        </div>
      </div>
    </figure>

    <div class="row clearfix">
      <article>
        <div>
          <p><strong>NSW</strong> achieved the highest market share in revenue and attendance amongst all states and territories in Australia, accounting for 35.6% and 31.8% of the national total respectively.</p>
          <p><strong>VIC</strong> represented the second highest market share of revenue and attendance amongst all the states and territories in Australia, accounting for 32.5% and 31.0% of the national total respectively.</p>
          <p>Combined, <strong>NSW</strong> and <strong>VIC</strong> generated approximately 68.1% of Australia’s live performance revenue and 62.8% of attendance in 2025.</p>
          <p>National spend per capita in 2025 was $120.57. <strong>VIC</strong> and <strong>NSW</strong> held the top positions in spend per capita in 2025, with $152.82 (26.8% higher than national spend per capita) and $138.20 (14.6% higher than national spend per capita), respectively<strong>.</strong></p>
          <p>Historically <strong>NSW</strong> and <strong>VIC</strong> have accounted for the largest market share of revenue and attendance across most categories. Notable exceptions to this in 2025 included:</p>
          <ul>
            <li><strong>Festivals (Multi-Category)</strong>, where <strong>SA</strong> and <strong>WA</strong> jointly accounted for 51.6% of the national market share of revenue and 62.0% of the national market share of attendance. Major annual festivals held in <strong>SA</strong> and <strong>WA</strong> included Adelaide Festival, Adelaide Fringe, Adelaide Beer &amp; BBQ Festival, Fringe World Festival, Perth Festival and Oktoberfest in the Gardens (Perth).</li>
            <li><strong>Special Events</strong>, where <strong>QLD</strong> and <strong>VIC</strong> combined accounted for the largest market shares of revenue (61.7%) and attendance (70.3%). Top events included <em>RuPaul's Drag Race</em>, <em>The Christmas Spectacular</em>, <em>RocKwiz Live!</em> and <em>The Greatest Christmas Show</em>.</li>
            <li><strong>Circus and Physical Theatre</strong>, where <strong>NSW</strong> and <strong>QLD</strong> jointly accounted for 53.7% of the national market share of revenue and 54.6% of the national market share of attendance. Top events included <em>Cirque du Soleil: Corteo</em>, <em>Now You See Me Live</em>, <em>Blanc de Blanc Encore</em> and <em>Cirque Alice</em>.</li>
          </ul>
        </div>

        <figure class="chart">
          <div class="callouts clearfix" v-if="activeReport">
            <aside id="state-per-capita-toprev">
              <h3 style="padding-top: .35em">Top Revenue Share</h3>
              <h2>
                <ICountUp :endVal="activeReport.topRevenueShare.value" :options="{decimalPlaces: 1}" />%
              </h2>
              <h3>{{ activeReport.topRevenueShare.state }}</h3>
            </aside>
            <aside id="state-per-capita-topatt">
              <h3 style="padding-top: .35em">Top Attendance Share</h3>
              <h2>
                <ICountUp :endVal="activeReport.topAttendanceShare.value" :options="{decimalPlaces: 1}" />%
              </h2>
              <h3>{{ activeReport.topAttendanceShare.state }}</h3>
            </aside>
            <aside id="state-per-capita-topspend">
              <h3 style="padding-top: .35em">Highest Spend Per Capita</h3>
              <h2>
                $
                <ICountUp :endVal="activeReport.highestSpendPerCapita.value" :options="{decimalPlaces: 2}" />
              </h2>
              <h3>{{ activeReport.highestSpendPerCapita.state }}</h3>
            </aside>
          </div>
        </figure>
      </article>
    </div>

    <div class="fullbg">
      <p class="caption">Photo: <em>Looking for Alibrandi - Ashton Malcolm</em>, Brink Productions, Photo Credit: Matt Byrne</p>
    </div>
  </section>
</template>

<script>
/*eslint-disable */
export default {
  data () {
    return {
      activeYear: 2025,
      reports: [
        {
          year: 2025,
          topRevenueShare: {
            state: 'New South Wales',
            value: 35.6
          },
          topAttendanceShare: {
            state: 'New South Wales',
            value: 31.8
          },
          highestSpendPerCapita: {
            state: 'Victoria',
            value: 152.82
          }
        },
      ],
      activeYear2: 2025,
      years: [2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025],
      reports2: [
        {
          year: 2025,
          data: [
            // z = population, x = revenue, y = attendance
            { name: 'NSW', z: 31.1, x: 35.6, y: 31.8, spend_pc: 138.20 },
            { name: 'VIC', z: 25.6, x: 32.5, y: 31.0, spend_pc: 152.82 },
            { name: 'QLD', z: 20.6, x: 15.1, y: 15.2, spend_pc: 88.65 },
            { name: 'WA', z: 11.1, x: 8.9, y: 9.9, spend_pc: 96.59 },
            { name: 'SA', z: 6.9, x: 5.7, y: 8.6, spend_pc: 99.29 },
            {
              name: 'ACT & TAS & NT', z: 4.9, x: 2.3, y: 3.5,
              data: [
                { name: 'ACT', z: 1.8, x: 1.1, y: 1.3, spend_pc: 78.30 },
                { name: 'TAS', z: 2.1, x: 0.9, y: 1.7, spend_pc: 49.77 },
                { name: 'NT', z: 1.0, x: 0.3, y: 0.5, spend_pc: 32.08 }
              ]
            }
          ]
        },
        {
          year: 2024,
          data: [
            // z = population, x = revenue, y = attendance
            { name: 'NSW', z: 31.2, x: 36.2, y: 32.4, spend_pc: 142.11 },
            { name: 'VIC', z: 25.6, x: 33.7, y: 31.4, spend_pc: 161.37 },
            { name: 'QLD', z: 20.5, x: 15.3, y: 15.7, spend_pc: 91.48 },
            { name: 'WA', z: 11.0, x: 7.4, y: 9.2, spend_pc: 82.95 },
            { name: 'SA', z: 6.9, x: 5.2, y: 8.0, spend_pc: 92.80 },
            {
              name: 'ACT & TAS & NT', z: 4.9, x: 2.1, y: 3.2,
              data: [
                { name: 'ACT', z: 1.8, x: 0.9, y: 1.2, spend_pc: 62.71 },
                { name: 'TAS', z: 2.1, x: 0.9, y: 1.3, spend_pc: 52.69 },
                { name: 'NT', z: 1.0, x: 0.3, y: 0.7, spend_pc: 32.04 }
              ]
            }
          ]
        },
        {
          year: 2023,
          data: [
            // z = population, x = revenue, y = attendance
            { name: 'NSW', z: 31.3, x: 33.3, y: 30.6, spend_pc: 123.76 },
            { name: 'VIC', z: 25.6, x: 31.4, y: 30.6, spend_pc: 142.58 },
            { name: 'QLD', z: 20.5, x: 17.2, y: 16.4, spend_pc: 97.55 },
            { name: 'WA', z: 10.9, x: 10.0, y: 10.7, spend_pc: 106.77 },
            { name: 'SA', z: 6.9, x: 5.8, y: 8.3, spend_pc: 97.24 },
            {
              name: 'ACT & TAS & NT', z: 4.7, x: 2.4, y: 3.5,
              data: [
                { name: 'ACT', z: 1.7, x: 1.2, y: 1.3, spend_pc: 78.09 },
                { name: 'TAS', z: 2.1, x: 0.9, y: 1.7, spend_pc: 50.70 },
                { name: 'NT', z: 0.9, x: 0.3, y: 0.5, spend_pc: 34.67 }
              ]
            }
          ]
        },
        {
          year: 2022,
          data: [
            // z = population, x = revenue, y = attendance
            { name: 'NSW', z: 31.4, x: 33.7, y: 30.0, spend_pc: 82.07 },
            { name: 'VIC', z: 25.5, x: 34.1, y: 32.4, spend_pc: 102.08 },
            { name: 'QLD', z: 20.5, x: 15.1, y: 15.4, spend_pc: 56.34 },
            { name: 'WA', z: 10.8, x: 8.0, y: 9.2, spend_pc: 56.83 },
            { name: 'SA', z: 7.0, x: 5.9, y: 8.6, spend_pc: 64.33 },
            {
              name: 'ACT & TAS & NT', z: 5.0, x: 3.2, y: 4.4,
              data: [
                { name: 'ACT', z: 1.8, x: 1.6, y: 1.6, spend_pc: 71.74 },
                { name: 'TAS', z: 2.2, x: 1.3, y: 2.2, spend_pc: 44.27 },
                { name: 'NT', z: 1.0, x: 0.3, y: 0.6, spend_pc: 27.72 }
              ]
            }
          ]
        },
        {
          year: 2021,
          data: [
            // z = population, x = revenue, y = attendance
            { name: 'NSW', z: 31.4, x: 32.0, y: 24.0, spend_pc: 26.82 },
            { name: 'VIC', z: 25.5, x: 23.3, y: 21.8, spend_pc: 24.08 },
            { name: 'QLD', z: 20.4, x: 19.6, y: 20.4, spend_pc: 25.24 },
            { name: 'WA', z: 10.7, x: 12.5, y: 15.0, spend_pc: 30.77 },
            { name: 'SA', z: 7.0, x: 8.0, y: 12.5, spend_pc: 30.01 },
            {
              name: 'ACT & TAS & NT', z: 5.0, x: 4.7, y: 6.3,
              data: [
                { name: 'ACT', z: 1.8, x: 1.8, y: 2.0, spend_pc: 26.72 },
                { name: 'TAS', z: 2.2, x: 2.1, y: 3.2, spend_pc: 24.95 },
                { name: 'NT', z: 1.0, x: 0.8, y: 1.1, spend_pc: 21.05 }
              ]
            }
          ]
        },
        {
          year: 2020,
          data: [
            // z = population, x = revenue, y = attendance
            { name: 'NSW', z: 31.8, x: 30.1, y: 25.3, spend_pc: 22.33 },
            { name: 'VIC', z: 25.9, x: 26.1, y: 19.9, spend_pc: 23.76 },
            { name: 'QLD', z: 20.2, x: 19.3, y: 16.2, spend_pc: 22.50 },
            { name: 'WA', z: 10.4, x: 12.0, y: 16.6, spend_pc: 27.24 },
            { name: 'SA', z: 6.9, x: 9.9, y: 18.0, spend_pc: 33.71 },
            {
              name: 'ACT & TAS & NT', z: 4.8, x: 2.5, y: 4.0,
              data: [
                { name: 'ACT', z: 1.7, x: 0.9, y: 1.3, spend_pc: 13.21 },
                { name: 'TAS', z: 2.1, x: 1.5, y: 2.4, spend_pc: 17.18 },
                { name: 'NT', z: 1.0, x: 0.1, y: 0.3, spend_pc: 3.45 }
              ]
            }
          ]
        },
        {
          year: 2019,
          data: [
            { name: 'NSW', z: 31.8, x: 33.9, y: 30.0, spend_pc: 81.94 },
            { name: 'VIC', z: 26.1, x: 31.9, y: 31.0, spend_pc: 94.19 },
            { name: 'QLD', z: 20.1, x: 14.6, y: 14.6, spend_pc: 55.71 },
            { name: 'WA', z: 10.4, x: 9.9, y: 11.1, spend_pc: 73.40 },
            { name: 'SA', z: 6.9, x: 6.8, y: 9.7, spend_pc: 75.77 },
            {
              name: 'ACT & TAS & NT', z: 4.8, x: 2.8, y: 3.6,
              data: [
                { name: 'ACT', z: 1.7, x: 1.6, y: 1.8, spend_pc: 73.60 },
                { name: 'TAS', z: 2.1, x: 1.1, y: 1.5, spend_pc: 41.84 },
                { name: 'NT', z: 1.0, x: 0.1, y: 0.3, spend_pc: 10.82 }
              ]
            }
          ]
        },
        {
          year: 2018,
          data: [
            {name: 'NSW', z: 32.0, x: 34.9, y: 31.3, spend_pc: 93.67},
            {name: 'VIC', z: 25.9, x: 32.3, y: 30.5, spend_pc: 107.08},
            {name: 'QLD', z: 20.1, x: 14.7, y: 14.6, spend_pc: 62.77},
            {name: 'WA', z: 10.4, x: 9.8, y: 10.6, spend_pc: 81.12},
            {name: 'SA', z: 6.9, x: 6.1, y: 8.4, spend_pc: 75.49},
            {name: 'ACT & TAS & NT', z: 4.8, x: 2.3, y: 4.6, data: [
              {name: 'ACT', z: 1.7, x: 1.3, y: 1.5, spend_pc: 65.71},
              {name: 'TAS', z: 2.1, x: 0.9, y: 2.8, spend_pc: 35.68},
              {name: 'NT', z: 1.0, x: 0.1, y: 0.3, spend_pc: 11.89}
            ]}
          ]
        },
        {
          year: 2017,
          data: [
            {name: 'NSW', z: 32.0, x: 32.7, y: 29.9, spend_pc: 77.81},
            {name: 'VIC', z: 25.8, x: 34.0, y: 32.0, spend_pc: 100.20},
            {name: 'QLD', z: 20.0, x: 14.0, y: 13.9, spend_pc: 53.15},
            {name: 'WA', z: 7.0, x: 10.4, y: 11.3, spend_pc: 113.81},
            {name: 'SA', z: 10.4, x: 6.6, y: 9.1, spend_pc: 47.79},
            {name: 'ACT & NT & TAS', z: 2.7, x: 1.5, y: 1.9, data: [
              {name: 'ACT', z: 1.7, x: 1.3, y: 1.6, spend_pc: 47.26},
              {name: 'NT', z: 1.0, x: 0.2, y: 0.3, spend_pc: 8.06},
              {name: 'TAS', z: 2.1, x: 0.9, y: 1.9, spend_pc: 65.68}
            ]}
          ]
        },
        {
          year: 2016,
          data: [
            {name: 'NSW', z: 32.0, x: 37.1, y: 32.3, spend_pc: 67.99},
            {name: 'VIC', z: 25.6, x: 30.8, y: 28.9, spend_pc: 70.52},
            {name: 'QLD', z: 20.0, x: 12.6, y: 13.5, spend_pc: 36.92},
            {name: 'WA', z: 10.5, x: 10.3, y: 12.4, spend_pc: 57.56},
            {name: 'SA', z: 7.0, x: 6.6, y: 9.6, spend_pc: 54.93},
            {name: 'TAS', z: 2.1, x: 1.0, y: 1.3, spend_pc: 60.18},
            {name: 'ACT & NT', z: 0.0, x: 0.0, y: 0.0, data: [
              {name: 'ACT', z: 1.7, x: 1.4, y: 1.7, spend_pc: 37.69},
              {name: 'NT', z: 1.0, x: 0.2, y: 0.3, spend_pc: 7.74}
            ]}
          ]
        },
        {
          year: 2015,
          data: [
            {name: 'NSW', z: 32.0, x: 34.0, y: 30.4, spend_pc: 62.33},
            {name: 'VIC', z: 25.1, x: 31.1, y: 30.2, spend_pc: 72.87},
            {name: 'QLD', z: 20.1, x: 13.9, y: 15.9, spend_pc: 40.62},
            {name: 'WA', z: 10.9, x: 11.5, y: 10.7, spend_pc: 62.40},
            {name: 'SA', z: 7.1, x: 6.9, y: 9.2, spend_pc: 56.85},
            {name: 'TAS', z: 2.2, x: 0.8, y: 1.1, spend_pc: 43.61},
            {name: 'ACT & NT', z: 0.0, x: 0.0, y: 0.0, data: [
              {name: 'ACT', z: 1.6, x: 1.7, y: 2.0, spend_pc: 45.43},
              {name: 'NT', z: 1.0, x: 0.2, y: 0.4, spend_pc: 8.19}
            ]}
          ]
        },
        {
          year: 2014,
          data: [
            {name: 'NSW', z: 32.0, x: 36.3, y: 33.1, spend_pc: 73.29},
            {name: 'VIC', z: 24.9, x: 29.8, y: 28.7, spend_pc: 77.71},
            {name: 'QLD', z: 20.1, x: 13.5, y: 14.4, spend_pc: 43.47},
            {name: 'WA', z: 10.9, x: 11.8, y: 12.2, spend_pc: 69.52},
            {name: 'SA', z: 7.2, x: 6.7, y: 8.7, spend_pc: 60.18},
            {name: 'ACT & NT', z: 2.6, x: 1.1, y: 1.9, data: [
              {name: 'ACT', z: 1.6, x: 0.9, y: 1.3, spend_pc: 36.06},
              {name: 'NT', z: 1.0, x: 0.2, y: 0.6, spend_pc: 12.84}
            ]},
            {name: 'TAS', z: 2.2, x: 0.8, y: 1.0, spend_pc: 22.57}
          ]
        },
        {
          year: 2013,
          data: [
            {name: 'NSW', z: 32.0, x: 33.6, y: 32.7, spend_pc: 66.64},
            {name: 'VIC', z: 24.8, x: 32, y: 31.3, spend_pc: 81.77},
            {name: 'QLD', z: 20.1, x: 13.5, y: 12.9, spend_pc: 42.67},
            {name: 'WA', z: 10.9, x: 13.1, y: 12, spend_pc: 76.17},
            {name: 'SA', z: 7.2, x: 6.3, y: 8.4, spend_pc: 55.96},
            {name: 'ACT & NT', z: 2.6, x: 1.1, y: 2.3, data: [
              {name: 'ACT', z: 1.6, x: 1.0, y: 2.0, spend_pc: 39.32},
              {name: 'NT', z: 1.0, x: 0.1, y: 0.3, spend_pc: 8.80}
            ]},
            {name: 'TAS', z: 2.2, x: 0.2, y: 0.4, spend_pc: 4.68}
          ]
        },
        {
          year: 2012,
          data: [
            {name: 'NSW', z: 32.1, x: 37, y: 40.1, spend_pc: 79.79},
            {name: 'VIC', z: 24.8, x: 28.6, y: 29.5, spend_pc: 74.36},
            {name: 'QLD', z: 20.1, x: 15.3, y: 12.7, spend_pc: 51.31},
            {name: 'WA', z: 10.8, x: 11.1, y: 9.9, spend_pc: 65.45},
            {name: 'SA', z: 7.3, x: 6.1, y: 5.9, spend_pc: 52.95},
            {name: 'ACT & NT', z: 2.7, x: 1.5, y: 1.3, data: [
              {name: 'ACT', z: 1.7, x: 1.4, y: 1.2, spend_pc: 51.00},
              {name: 'NT', z: 1.0, x: 0.1, y: 0.1, spend_pc: 6.62}
            ]},
            {name: 'TAS', z: 2.2, x: 0.4, y: 0.5, spend_pc: 25.97}
          ]
        },
      ],
    }
  },
  computed: {
    activeReport () {
      const self = this

      for (let i = 0; i < self.reports.length; i++) {
        const report = self.reports[i]
        if (report.year === self.activeYear) {
          return report
        }
      }

      return false
    },
    bubbleChart () {
      const self = this

      for (let i = 0; i < self.reports2.length; i++) {
        const report = self.reports2[i]
        if (report.year === self.activeYear2) {
          return {
            chart: {
              type: 'bubble',
              backgroundColor: 'transparent'
            },
            title: {
              text: ''
            },
            yAxis: {
              title: {
                text: 'Revenue Share',
                style: {
                  color: '#fff'
                }
              },
              labels: {
                style: {
                  color: '#fff'
                },
                formatter () {
                  return this.value + '%'
                }
              },
              max: 45,
              min: 0.1,
              gridLineColor: 'rgba(255,255,255, 0.4)'
            },
            xAxis: {
              title: {
                text: 'Attendance Share',
                style: {
                  color: '#fff'
                }
              },
              labels: {
                style: {
                  color: '#fff'
                },
                formatter () {
                  return this.value + '%'
                }
              },
              max: 45,
              gridLineColor: '#fff',
              lineColor: '#fff'
            },
            credits: {
              enabled: false
            },
            legend: {
              enabled: false
            },
            tooltip: {
              shared: true,
              valueSuffix: '%',
              formatter () {
                if (this.point.data) {
                  let str = '', pointData = this.point.data,
                    length = pointData.length;
                  for (let i=0; i<length; i++) {
                    let data = pointData[i]
                    str += (i > 0 ? '<br />' : '') + data.name
                      + ' <br />Attendance: ' + data.y.toFixed(1) + '%'
                      + ' <br />Revenue: ' + data.x.toFixed(1) + '%'
                      + ' <br />Population: ' + data.z.toFixed(1) + '%'
                      + ' <br /> Spend Per Capita: $' + data.spend_pc.toFixed(2)
                  }
                  return str;
                }
                else {
                  return this.point.name
                    + ' <br />Attendance: ' + this.point.y.toFixed(1) + '%'
                    + ' <br />Revenue: ' + this.point.x.toFixed(1) + '%'
                    + ' <br />Population: ' + this.point.z.toFixed(1) + '%'
                    + ' <br />Spend Per Capita: $' +  this.point.spend_pc.toFixed(2)
                }
              },
              style: {
                color: '#000'
              }
            },
            plotOptions: {
              bubble: {
                dataLabels: {
                  enabled: true,
                  style: {
                    textShadow: 'none',
                    color: '#fff'
                  },
                  formatter () {
                    return this.point.name
                  }
                }
              }
            },
            series: [{
              name: 'Per Capita Data',
              data: report.data,
              marker: {
                fillColor: 'rgba(60,230,255,0.8)',
                lineColor: 'rgba(60,230,255,1)'
              }
            }]
          }
        }
      }

      return false
    }
  },
  mounted () {
  },
  methods: {
  }
}
</script>

<style lang="scss" scoped="scoped">
#state-per-capita .fullbg {
  background-image: url("~@/assets/img/2025/6-Percentage-Market-Looking-for-Alibrandi-2025-2-Photo-by-Matt-Byrne.jpg");
}
.chart-wrapper {
  position: relative;
  .nb {
    position: absolute;
    bottom: -10px;
    right: -60px;
    color: #fff;
    font-style: italic;
    font-size: 12px;
    @media (max-width: 768px) {
      position: unset;
    }
  }
}
</style>
